import React from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Tile } from '@carbon/react'
import { chartSeriesHexByIndex } from '@sphere/shared/util'
import styles from './ChartWrapper.module.scss'

export interface LineChartWrapperProps {
  data: Array<Record<string, any>>
  dataKey: string
  lines: Array<{
    dataKey: string
    name: string
    stroke?: string
    strokeWidth?: number
  }>
  xAxisKey?: string
  title?: string
  height?: number
  yAxisDomain?: [number | string, number | string]
  /** When false, Y-axis ticks are integers only (Recharts `YAxis.allowDecimals`). Use for counts. */
  yAxisAllowDecimals?: boolean
  xAxisFormatter?: (value: any) => string
  tooltipFormatter?: (value: any, name: string) => [any, string]
  tooltipLabelFormatter?: (label: any) => string
}

export function LineChartWrapper({
  data,
  dataKey,
  lines,
  xAxisKey = 'date',
  title,
  height = 300,
  yAxisDomain,
  yAxisAllowDecimals,
  xAxisFormatter,
  tooltipFormatter,
  tooltipLabelFormatter,
}: LineChartWrapperProps) {
  const content = (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisKey} tickFormatter={xAxisFormatter} />
        <YAxis
          domain={yAxisDomain}
          allowDecimals={yAxisAllowDecimals ?? true}
        />
        <Tooltip
          formatter={
            tooltipFormatter
              ? (value, name, ...rest) =>
                  tooltipFormatter(
                    value,
                    typeof name === 'string' ? name : String(name)
                  )
              : undefined
          }
          labelFormatter={tooltipLabelFormatter}
        />

        <Legend />
        {lines.map((line, index) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            stroke={line.stroke || chartSeriesHexByIndex(index)}
            strokeWidth={line.strokeWidth || 2}
            name={line.name}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )

  if (title) {
    return (
      <Tile className={styles.chartTile}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {content}
      </Tile>
    )
  }

  return <div className={styles.chartContainer}>{content}</div>
}
